import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { Building2, Search, RefreshCcw, Printer } from "lucide-react";

const BASE_URL = "https://nissi-india.com";

// ---------- helpers ----------
const s = (v) => (v ?? "").toString().trim();

// dd/MM/yyyy -> yyyy-MM-dd (for input type="date")
const ddmmyyyyToISO = (ddmmyyyy) => {
  const v = s(ddmmyyyy);
  if (!v) return "";
  const m = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return ""; // if not in dd/MM/yyyy, ignore
  const [, dd, mm, yyyy] = m;
  return `${yyyy}-${mm}-${dd}`;
};

const WeeklyBattaAbstractReport = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const loginBranchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "HO";

  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("Select");

  // ✅ keep these always as YYYY-MM-DD (because input type="date")
  const [fromDate, setFromDate] = useState(""); // YYYY-MM-DD
  const [toDate, setToDate] = useState("");     // YYYY-MM-DD

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [pageTotal, setPageTotal] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);

  const screenBadge = useMemo(() => "INFRA SCREEN : C-34", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const fetchMeta = async () => {
    if (!s(finYearId)) return toast.warn("Please select Financial Year.");
    if (!s(loginBranchId)) return toast.warn("Missing login branch.");

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/weekly-batta-abstract/meta`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(loginBranchId),
          branchType: String(branchType),
        },
      });

      if (res?.data?.success) {
        setBranches(res.data.branches || []);
        setBranchId(String(res.data.defaultBranchId || "Select"));

        // ✅ API returns dd/MM/yyyy -> convert to yyyy-MM-dd for date input
        setFromDate(ddmmyyyyToISO(res.data.defaultFromDate || ""));
        setToDate(ddmmyyyyToISO(res.data.defaultToDate || ""));
      } else {
        toast.error(res?.data?.message || "Failed to load meta.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Meta error");
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async (p = page) => {
    if (!s(finYearId)) return toast.warn("Please select Financial Year.");
    if (!s(loginBranchId)) return toast.warn("Missing login branch.");

    // ASP validation requires branch
    if (!branchId || branchId === "Select") {
      toast.warn("Please select Branch...");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/weekly-batta-abstract/data`, {
        params: {
          finYearId: Number(finYearId),
          loginBranchId: Number(loginBranchId),
          branchId: String(branchId),

          // ✅ send YYYY-MM-DD (Node already supports it & converts to dd/MM/yyyy)
          fromDate: String(fromDate || ""),
          toDate: String(toDate || ""),

          page: p,
          pageSize,
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
        setPage(res.data.page || p);
        setPageTotal(Number(res.data.pageTotal || 0));
        setGrandTotal(Number(res.data.grandTotal || 0));
      } else {
        toast.error(res?.data?.message || "Failed to load data.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Data error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (page > 1) fetchData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    setPage(1);
    await fetchData(1);
  };

  const handleRefresh = async () => {
    await fetchMeta();
    setPage(1);
    setRows([]);
    setTotal(0);
    setTotalPages(1);
    setPageTotal(0);
    setGrandTotal(0);
  };

  const handlePrint = () => {
    if (!branchId || branchId === "Select") {
      toast.warn("Please select Branch...");
      return;
    }

    const url =
      `/PayRoll/WeeklyBattaAbstractReport_Print` +
      `?finYearId=${encodeURIComponent(finYearId)}` +
      `&loginBranchId=${encodeURIComponent(loginBranchId)}` +
      `&branchType=${encodeURIComponent(branchType)}` +
      `&branchId=${encodeURIComponent(branchId)}` +
      `&fromDate=${encodeURIComponent(fromDate || "")}` + // YYYY-MM-DD ok
      `&toDate=${encodeURIComponent(toDate || "")}`;

    window.open(url, "_blank", "width=900,height=900,scrollbars=yes,resizable=yes");
  };

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  return (
    <PayRollLayoutWrapper>
      <ToastContainer position="top-right" autoClose={2800} theme={isDark ? "dark" : "light"} />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">WEEKLY BATTA ABSTRACT REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                <Building2 className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Weekly Batta Abstract
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Branch + Date Range + Pagination + Print
                </div>
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                {screenBadge}
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"}`}>
                {total} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={handleRefresh} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              <button onClick={handlePrint} className={primaryBtn} disabled={loading}>
                <Printer className="w-4 h-4" />
                Print
              </button>
            </div>
          </div>

          {/* Filters */}
          <form onSubmit={handleSearch} className={cardBase + " p-6 mb-6"}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div>
                <label className={labelBase}>
                  Branch <span className="text-red-500">*</span>
                </label>
                <select value={branchId} onChange={(e) => setBranchId(e.target.value)} className={inputBase}>
                  <option value="Select">Select</option>
                  {branches.map((b) => (
                    <option key={String(b.fld_Branchid)} value={String(b.fld_Branchid)}>
                      {b.fld_BranchName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelBase}>From Date</label>
                <input
                  type="date"
                  value={fromDate} // YYYY-MM-DD
                  onChange={(e) => setFromDate(e.target.value)}
                  className={inputBase}
                />
              </div>

              <div>
                <label className={labelBase}>To Date</label>
                <input
                  type="date"
                  value={toDate} // YYYY-MM-DD
                  onChange={(e) => setToDate(e.target.value)}
                  className={inputBase}
                />
              </div>
            </div>

            <div className="flex justify-center mt-6">
              <button type="submit" className={primaryBtn} disabled={loading}>
                <Search className="w-4 h-4" />
                Search
              </button>
            </div>
          </form>

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold w-[120px]">S.No</th>
                    <th className="px-4 py-4 text-center font-bold">Department</th>
                    <th className="px-4 py-4 text-center font-bold w-[220px]">Amount</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        <td className="px-4 py-4"><div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" /></td>
                        <td className="px-4 py-4"><div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" /></td>
                        <td className="px-4 py-4"><div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" /></td>
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={3} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr key={idx} className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {(page - 1) * pageSize + idx + 1}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_department ?? r.Department ?? ""}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_receivedamount ?? r.Amount ?? ""}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>

                <tfoot>
                  <tr className={isDark ? "bg-gray-800/40" : "bg-gray-50"}>
                    <td className="px-4 py-4 text-right font-extrabold" colSpan={2}>
                      Total
                    </td>
                    <td className="px-4 py-4 text-center font-extrabold">{pageTotal}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
              &nbsp; | &nbsp; Grand Total: <b>{grandTotal}</b>
            </div>

            {totalPages > 1 && (
              <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === 1 ? "text-gray-400 cursor-not-allowed" : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Prev
                </button>

                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i + 1)}
                    className={`px-4 py-2 text-sm font-bold transition ${
                      page === i + 1 ? `${primaryBtnClass} text-white` : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === totalPages ? "text-gray-400 cursor-not-allowed" : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default WeeklyBattaAbstractReport;